import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Container, Grid, Box, TextField, Button, Divider, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppKit } from '@reown/appkit/react';
import { useAccount, useDisconnect } from 'wagmi';
import { useGoogleLogin } from '@react-oauth/google';
import Navigations from '../Navigations';
import Heading from '../../../Components/Heading';
import googlebutton from '../../../../assets/googlebutton.svg';
import walletbutton from '../../../../assets/walletbutton.svg';
import Paragraph from '../../../Components/Paragraph';
import circletick from '../../../../assets/circletick.png';
import yeartop from '../../../../assets/paymentright.png';
import shield from '../../../../assets/shield.png';
import metamask from '../../../../assets/metamask.svg';
import walletconnect from '../../../../assets/walletconnect.svg';
import coinbase from '../../../../assets/coinbase.svg';
import trust from '../../../../assets/trust.svg';
import phantom from '../../../../assets/phantom.svg';
import ledger from '../../../../assets/ledger.svg';
import web3 from '../../../../assets/web3.svg';

const Wrapper = styled.div`
  min-height: 100vh;
  padding-top: 40px;
  color: #fff;
`;

const Card = styled(Box)`
  background: #080411;
  border-radius: 20px;
  padding: 30px;
  border: 1px solid #1f1f2e;
  width: 100%;
  margin-top: ${({ marginTop }) => marginTop || 0};
`;

const GoogleButton = styled(Button)`
  border: 1px solid #2b2b3d;
  padding: 14px;
  border-radius: 8px;
  color: #fff !important;
  text-transform: none;
  gap: 10px;
  font-size: 17px;
  background-color: rgba(255, 255, 255, 0.05);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  &:hover {
    border-color: #7300FF;
  }
`;

const WalletButton = styled(Button)`
  border: 1px solid #2b2b3d;
  padding: 14px;
  border-radius: 8px;
  color: #fff !important;
  text-transform: none;
  gap: 10px;
  font-size: 17px;
  background-color: rgba(255, 255, 255, 0.05);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  &:hover {
    border-color: #7300FF;
  }
`;

const MonthlyPlan = styled(Box)`
  border: ${({ selected }) => (selected ? '2px solid #7300FF' : '1px solid #2b2b3d')};
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s;
  &:hover {
    border-color: #7300FF;
  }
`;

const YearlyPlan = styled(Box)`
  border: ${({ selected }) => (selected ? '2px solid #7300FF' : '1px solid #2b2b3d')};
  border-radius: 20px;
  padding: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  transition: all 0.2s;
  margin-top: 20px;
  &:hover {
    border-color: #7300FF;
  }
`;

const PlanCircle = styled(Box)`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: ${({ selected }) => (selected ? 'none' : '2px solid #8a8a9c')};
  background: ${({ selected }) => (selected ? '#7300FF' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
`;

const PlanInnerCircle = styled(Box)`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
`;

const MostPopularBadge = styled(Box)`
  position: absolute;
  top: -12px;
  left: 20px;
  background: linear-gradient(90deg, #a855ff, #7b2ff7);
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
`;

const SaveBadge = styled(Box)`
  background: rgba(34, 197, 94, 0.2);
  color: #05DF72;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 12px;
`;

const DividerWrapper = styled(Box)`
  display: flex;
  align-items: center;
  margin: 20px 0 25px;
  & .MuiDivider-root {
    border-bottom-width: 1px;
    border-color: #4a484874;
  }
`;

const TextFieldStyled = styled(TextField)`
  margin-top: 10px;
  & .MuiOutlinedInput-root {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    border: 1px solid #2b2b3d;
    input {
      color: #ffffff;
    }
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: #7300FF;
      border-width: 1px;
    }
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: #7300FF;
    }
  }
`;

const WalletInfoBox = styled(Box)`
  margin-top: 10px;
  background: rgba(115, 0, 255, 0.1);
  border: 1px solid #7300FF;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const WalletIconWrapper = styled(Box)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
`;

const DisconnectButton = styled(Button)`
  color: #ff6b6b;
  text-transform: none;
  font-size: 12px;
  padding: 4px 12px;
  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
`;

const SignInLink = styled.span`
  color: #b57bff;
  cursor: pointer;
`;

const GradientHeadingSpan = styled.span`
  background: linear-gradient(149deg, #fff 33%, #a860ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: bold;
`;

const StyledButton = styled(Button)`
  text-transform: none;
  color: white;
  font-family: "PoppinsM";
  width: 100%;
  border-radius: 40px !important;
  font-size: 16px;
  font-family: Bold !important;
  padding: ${(props) => (props.p ? props.p : "6px 20px")};
  border: 3px solid #220154;

  &:hover {
    background: linear-gradient(90deg, #7300FF 0%, #B892ED 100%);
    color: white;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  @media(max-width: 599px) {
    font-size: 14px !important;
    padding: 7px 9px !important;
  }
`;

const UpperBox = styled(Box)`
  background: linear-gradient(90deg, #7300FF 0%, #B892ED 100%);
  padding: 2px;
  border-radius: 40px;
  width: 100%;
`;
const DisconnectBtn = styled.button`
  width: 100%;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 14px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  margin-top: 20px;
   color: #ff6b6b;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(255, 107, 107, 0.4);
    background: rgba(255, 107, 107, 0.05);
    color: #ff6b6b;
  }
`;

const CYAHeader = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [email, setEmail] = useState('');
  const [googleUser, setGoogleUser] = useState(null);
  
  // Wallet connection hooks
  const { open } = useAppKit();
  const { address, isConnected, connector } = useAccount();
  const { disconnect } = useDisconnect();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (location.state?.selectedPlan) {
      setSelectedPlan(location.state.selectedPlan);
      sessionStorage.setItem('selectedPlan', location.state.selectedPlan);
    } else {
      const savedPlan = sessionStorage.getItem('selectedPlan');
      if (savedPlan) {
        setSelectedPlan(savedPlan);
      }
    }

    // Load Google user data from sessionStorage
    const savedGoogleUser = sessionStorage.getItem('googleUser');
    if (savedGoogleUser) {
      const userData = JSON.parse(savedGoogleUser);
      setGoogleUser(userData);
      setEmail(userData.email);
    }

    // Load email from sessionStorage
    const savedEmail = sessionStorage.getItem('userEmail');
    if (savedEmail && !savedGoogleUser) {
      setEmail(savedEmail);
    }
  }, [location.state]);

  // Save plan to sessionStorage when it changes
  useEffect(() => {
    if (selectedPlan) {
      sessionStorage.setItem('selectedPlan', selectedPlan);
    }
  }, [selectedPlan]);

  // Save email to sessionStorage when it changes
  useEffect(() => {
    if (email && !googleUser) {
      sessionStorage.setItem('userEmail', email);
    }
  }, [email, googleUser]);

  // Google login handler
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      console.log('Google login success:', tokenResponse);
      
      try {
        // Fetch user info from Google
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        });
        
        const userInfo = await userInfoResponse.json();
        console.log('User info:', userInfo);
        
        // Store user info in state and sessionStorage
        setGoogleUser(userInfo);
        setEmail(userInfo.email);
        sessionStorage.setItem('googleUser', JSON.stringify(userInfo));
        sessionStorage.setItem('userEmail', userInfo.email);
        
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    },
    onError: (error) => {
      console.error('Google login failed:', error);
    },
  });

  // Get wallet name
  const getWalletName = () => {
    if (!connector) return 'Wallet';
    return connector.name || 'Connected Wallet';
  };

  // Get short address
  const getShortAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // Get wallet initials for icon
  const getWalletInitials = () => {
    const name = getWalletName();
    return name.slice(0, 2).toUpperCase();
  };

  const handleWalletConnect = () => {
    open();
  };

  const handleDisconnect = () => {
    disconnect();
  };

  const handleGoogleLogout = () => {
    setGoogleUser(null);
    setEmail('');
    sessionStorage.removeItem('googleUser');
    sessionStorage.removeItem('userEmail');
  };

  // Check if user is authenticated (either wallet or Google or email entered)
  const isAuthenticated = isConnected || googleUser || (email && email.includes('@'));

  // Handle payment button click
  const handlePaymentClick = () => {
    if (!isAuthenticated) {
      return; // Button is disabled, do nothing
    }

    if (!selectedPlan) {
      alert('Please select a plan to continue');
      return;
    }

    // Save authentication state before navigating
    const authState = {
      isWalletConnected: isConnected,
      walletAddress: address,
      googleUser: googleUser,
      email: email,
      selectedPlan: selectedPlan
    };

    // Navigate to payment method page
    navigate('/paymentmethod', { 
      state: authState
    });
    
  };
  // Add this helper function after your imports
const getWalletIcon = (connectorName) => {
  const walletIcons = {
    'MetaMask': metamask,
    'WalletConnect': walletconnect,
    'Coinbase Wallet': coinbase,
    'Trust Wallet': trust,
    'Phantom': phantom,
    'Ledger': ledger
  };

  const matchedKey = Object.keys(walletIcons).find(key => 
    connectorName?.toLowerCase().includes(key.toLowerCase())
  );

  return walletIcons[matchedKey] || web3;
};

  return (
    <Wrapper>
      <Navigations currentStep={2} />
      <Container maxWidth="xl">
        <Grid container justifyContent="center" mb={4}>
          <Heading>
            Create Your <GradientHeadingSpan>Account</GradientHeadingSpan>
          </Heading>
        </Grid>

        <Grid container spacing={4} justifyContent="center">
          {/* LEFT SIDE */}
          <Grid item xs={12} md={7}>
            <Card>
              <Box mb={2}>
                <label style={{ marginLeft: '4px' }}>
                  {isConnected ? 'Connected Wallet' : googleUser ? 'Google Account' : 'Email Address'}
                </label>
                
                {!isConnected && !googleUser ? (
                  <>
                    <TextFieldStyled 
                      fullWidth 
                      placeholder="your@email.com" 
                      variant="outlined" 
                      autoComplete="off"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <Box mt={1} fontSize="14px" color="#8a8a9c">
                      Already have an account? <a style={{ textDecoration: 'underline', color: '#7300FF' }}> <SignInLink>Sign in</SignInLink></a>
                    </Box>
                  </>
                ) : isConnected ? (
                  <>
                  <WalletInfoBox>
                    <Box display="flex" alignItems="center" gap={2}>
                      <img 
                      src={getWalletIcon(connector?.name)} 
                      alt={getWalletName()} 
                      style={{ 
                        width: '32px', 
                        height: '32px', 
                        borderRadius: '8px' 
                      }} 
                    />
                      <Box>
                        <Typography fontSize="16px">
                          {getWalletName()}
                        </Typography>
                        <Typography fontSize="14px" color="#b57bff">
                          {getShortAddress(address)}
                        </Typography>
                      </Box>
                    </Box>
                    {/* <DisconnectButton onClick={handleDisconnect}>
                      Disconnect
                    </DisconnectButton> */}
                  </WalletInfoBox>
                   <DisconnectBtn onClick={handleDisconnect}>
                    Disconnect
                    </DisconnectBtn>
                    </>
                ) : (
                  <>
                  <WalletInfoBox>
                    <Box display="flex" alignItems="center" gap={2}>
                      {googleUser.picture && (
                        <img 
                          src={googleUser.picture} 
                          alt="Profile" 
                          style={{ 
                            width: '32px', 
                            height: '32px', 
                            borderRadius: '50%' 
                          }} 
                        />
                      )}
                      <Box>
                        <Typography fontSize="16px">
                          {googleUser.name}
                        </Typography>
                        <Typography fontSize="14px" color="#b57bff">
                          {googleUser.email}
                        </Typography>
                      </Box>
                    </Box>
                    {/* <DisconnectButton  onClick={handleGoogleLogout}>
                      Sign Out
                    </DisconnectButton> */}
                  </WalletInfoBox>
                   <DisconnectBtn onClick={handleGoogleLogout}>
                    Signout 
                    </DisconnectBtn>
                    </>
                )}
              </Box>

              <DividerWrapper>
                <Divider sx={{ flex: 1 }} />
                <Typography variant="body2" sx={{ px: 2, color: '#6A7282', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                  Or continue with
                </Typography>
                <Divider sx={{ flex: 1 }} />
              </DividerWrapper>

              <GoogleButton 
                fullWidth 
                variant="outlined"
                onClick={() => googleLogin()}
                disabled={!!googleUser}
              >
                <img src={googlebutton} alt="button" style={{ width: '20px', height: 'auto' }} /> 
                {googleUser ? 'Google Account Connected' : 'Continue with Google'}
              </GoogleButton>
              
              <WalletButton 
                fullWidth 
                variant="outlined"
                onClick={handleWalletConnect}
                disabled={isConnected}
              >
                <img src={walletbutton} alt="button" style={{ width: '20px', height: 'auto' }} /> 
                {isConnected ? 'Wallet Connected' : 'Connect Wallet'}
              </WalletButton>
            </Card>

            <Card marginTop={'40px'}>
              <Paragraph style={{ fontSize: '23px' }}>Select Your Plan</Paragraph>

              {/* Monthly Plan */}
              <MonthlyPlan selected={selectedPlan === 'monthly'} onClick={() => setSelectedPlan('monthly')}>
                <Box display="flex" alignItems="center" gap={2}>
                  <PlanCircle selected={selectedPlan === 'monthly'}>
                    {selectedPlan === 'monthly' && <PlanInnerCircle />}
                  </PlanCircle>
                  <Box>
                    <Typography fontSize="18px" fontWeight={600}>Monthly</Typography>
                    <Typography fontSize="14px" color="#99A1AF">Billed every 1 month</Typography>
                  </Box>
                </Box>
                <Box textAlign="right">
                  <Typography fontSize="24px">$5.00</Typography>
                  <Typography fontSize="14px" color="#99A1AF">$5/month</Typography>
                </Box>
              </MonthlyPlan>

              {/* Yearly Plan */}
              <YearlyPlan selected={selectedPlan === 'yearly'} onClick={() => setSelectedPlan('yearly')}>
                <MostPopularBadge>Most Popular</MostPopularBadge>
                <Box display="flex" alignItems="center" gap={2}>
                  <PlanCircle selected={selectedPlan === 'yearly'}>
                    {selectedPlan === 'yearly' && <PlanInnerCircle />}
                  </PlanCircle>
                  <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontSize="18px" fontWeight={600}>1 Year</Typography>
                      <SaveBadge>Save 16%</SaveBadge>
                    </Box>
                    <Typography fontSize="14px" color="#99A1AF">Billed every 12 months</Typography>
                  </Box>
                </Box>
                <Box textAlign="right">
                  <Typography fontSize="24px">$50.00</Typography>
                  <Typography fontSize="14px" color="#99A1AF">$4.166/month</Typography>
                </Box>
              </YearlyPlan>
            </Card>

            <Card marginTop={'40px'}>
              <Paragraph style={{ fontSize: '23px' }}>What's Included</Paragraph>
              
              <Grid container spacing={3} mt={1}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <img src={circletick} alt="tick" style={{ width: '24px', height: '24px' }} />
                    <Paragraph style={{ marginBottom: '0px' }}>Unlimited bandwidth</Paragraph>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <img src={circletick} alt="tick" style={{ width: '24px', height: '24px' }} />
                    <Paragraph style={{ marginBottom: '0px' }}>Military-grade encryption</Paragraph>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <img src={circletick} alt="tick" style={{ width: '24px', height: '24px' }} />
                    <Paragraph style={{ marginBottom: '0px' }}>Kill switch protection</Paragraph>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <img src={circletick} alt="tick" style={{ width: '24px', height: '24px' }} />
                    <Paragraph style={{ marginBottom: '0px' }}>24/7 customer support</Paragraph>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <img src={circletick} alt="tick" style={{ width: '24px', height: '24px' }} />
                    <Paragraph style={{ marginBottom: '0px' }}>10 Gbps servers</Paragraph>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <img src={circletick} alt="tick" style={{ width: '24px', height: '24px' }} />
                    <Paragraph style={{ marginBottom: '0px' }}>No-logs policy</Paragraph>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <img src={circletick} alt="tick" style={{ width: '24px', height: '24px' }} />
                    <Paragraph style={{ marginBottom: '0px' }}>Split tunneling</Paragraph>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <img src={circletick} alt="tick" style={{ width: '24px', height: '24px' }} />
                    <Paragraph style={{ marginBottom: '0px' }}>Connect up to 5 devices</Paragraph>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* RIGHT SIDE — ORDER SUMMARY */}
          <Grid item xs={12} md={3.7}>
            <Card style={{ position: 'sticky',top: '14px' }}>
              <img 
                src={yeartop} 
                alt="decoration" 
                style={{ position: 'absolute', top: 0, right: 0, width: '100%',maxWidth: '600px', height: 'auto',}} />
              <Typography fontSize="20px" fontWeight={600} mb={3}>
                Order Summary
              </Typography>

              {/* Plan Details */}
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography color="#99A1AF" fontSize="16px">Plan</Typography>
                  <Typography fontSize="16px" zIndex={'1'}>
                    {selectedPlan === 'yearly' ? '1 Year' : selectedPlan === 'monthly' ? 'Monthly' : '-'}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography color="#99A1AF" fontSize="16px">Duration</Typography>
                  <Typography fontSize="16px" zIndex={'1'}>
                    {selectedPlan === 'yearly' ? '12 months' : selectedPlan === 'monthly' ? '1 month' : '-'}
                  </Typography>
                </Box>

              <Box display="flex" justifyContent="space-between" mb={3}>
                <Typography color="#99A1AF" fontSize="16px">Subtotal</Typography>
                <Typography fontSize="16px" zIndex={'1'}>
                  {selectedPlan === 'yearly' ? '$50.00' : selectedPlan === 'monthly' ? '$5.00' : '$0.00'}
                </Typography>
              </Box>
              
              <Divider sx={{ borderBottomWidth: '1px', borderColor: '#222222', marginTop: '20px', marginBottom: "25px" }} />
              
              {/* Voucher Section */}
              <Box mb={3}>
                <Typography fontSize="14px" mb={1} color={'#99A1AF'}>Have a voucher code?</Typography>
                <Box display="flex" alignItems="flex-end" gap={1}>
                  <TextFieldStyled 
                    fullWidth 
                    placeholder="Enter code" 
                    variant="outlined" 
                    size="small"
                    sx={{'& .MuiOutlinedInput-root': {fontSize: '14px',}}}/>
                  <Button 
                    variant="outlined" 
                    sx={{ 
                      borderColor: '#2b2b3d', 
                      color: '#fff',
                      textTransform: 'none',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        borderColor: '#7300FF'
                      }
                    }}
                  >
                    Apply
                  </Button>
                </Box>
                <Typography fontSize="13px" color="#99A1AF" mt={1}>
                  Try: SAVE20, WELCOME10, CRYPTO50
                </Typography>
              </Box>
              
              <Divider sx={{ borderBottomWidth: '1px', borderColor: '#222222', marginTop: '20px', marginBottom: "25px" }} />
              
              {/* Total */}
              <Box display="flex" justifyContent="space-between">
                <Typography fontSize="18px">Total</Typography>
                <Typography fontSize="24px" color="#C27AFF">
                  {selectedPlan === 'yearly' ? '$50.00' : selectedPlan === 'monthly' ? '$5.00' : '$0.00'}
                </Typography>
              </Box>
              
              <Divider sx={{ borderBottomWidth: '1px', borderColor: '#222222', marginTop: '20px', marginBottom: "25px" }} />
              
              <Box style={{ width: '100%' }}>
                <UpperBox>
                  <StyledButton 
                    onClick={handlePaymentClick}
                    disabled={!isAuthenticated || !selectedPlan}
                  >
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      {!isAuthenticated && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                      )}
                      {isAuthenticated ? 'Complete the Payment' : 'Sign in to Continue'}
                    </Box>
                  </StyledButton>
                </UpperBox>
              </Box>
              
              {/* Secure Payment */}
              <Box display="flex" alignItems="left" justifyContent="left" gap={1} mt={2} mb={3}>
                <img src={shield} alt="shield" style={{ width: '16px', height: '16px' }} />
                <Typography fontSize="13px" color="#99A1AF">Secure payment processing</Typography>
              </Box>

              {/* Payment Methods */}
              <Box textAlign="center">
                <Typography fontSize="14px" mb={1.5}>We accept</Typography>
                <Box display="flex" justifyContent="center" gap={1.5} flexWrap="wrap">
                  <Box sx={{ background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    $SVPN
                  </Box>
                  <Box sx={{ background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    $ETH
                  </Box>
                  <Box sx={{ background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    $USDC
                  </Box>
                  <Box sx={{ background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    $USDT
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Wrapper>
  );
};

export default CYAHeader;